import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Cookie consent.
 *
 * The Figma frame (2021:7305) is a dropped-in third-party kit component: it
 * uses Montserrat and Comfortaa and a teal #04BCCA toggle, none of which appear
 * anywhere else in the design. Rebuilding it literally would mean shipping two
 * extra webfonts and an off-palette colour for one banner, so the layout and
 * behaviour follow the design while the type and colour follow the site.
 *
 * ⚠️ This records a preference; it does not gate anything yet. Nothing on the
 * site currently sets a non-essential cookie — analytics arrive via GTM — so
 * before launch the tag manager needs to read `sob-cookie-consent` and hold
 * non-essential tags until consent is given. A banner that stores a choice and
 * then ignores it is worse than none.
 */
const STORAGE_KEY = 'sob-cookie-consent';

function read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
  } catch {
    // Private mode, blocked storage, corrupt value — all mean "not asked yet".
    return null;
  }
}

function write(value) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // A viewer who cannot store the choice still gets the banner dismissed for
    // this page view; re-asking next visit is the correct fallback.
  }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (!read()) setOpen(true);
  }, []);

  if (!open) return null;

  const save = (value) => {
    write({ ...value, at: new Date().toISOString() });
    setOpen(false);
  };

  return (
    <div className="consent" role="dialog" aria-label="Manage consent" aria-modal="false">
      <div className="consent__head">
        <h2 className="consent__title">Manage Consent</h2>
        <button
          type="button"
          className="consent__close"
          onClick={() => save({ analytics: false, marketing: false })}
          aria-label="Reject non-essential cookies and close"
        >
          ✕
        </button>
      </div>

      <p className="consent__body">
        To provide the best experiences, we use technologies like cookies to store and/or access
        device information. Consenting to these technologies will allow us to process data such as
        browsing behaviour or unique IDs on this site. Not consenting or withdrawing consent may
        adversely affect certain features and functions.
      </p>

      {expanded && (
        <div className="consent__optins">
          <span className="consent__optins-label">Opt-ins</span>
          <div className="consent__toggles">
            {[
              { key: 'analytics', label: 'Analytics' },
              { key: 'marketing', label: 'Marketing' },
            ].map(({ key, label }) => (
              <label className="toggle" key={key}>
                <input
                  type="checkbox"
                  checked={prefs[key]}
                  onChange={(e) => setPrefs((p) => ({ ...p, [key]: e.target.checked }))}
                />
                <span className="toggle__track" aria-hidden="true">
                  <span className="toggle__thumb" />
                </span>
                <span className="toggle__label">{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="consent__actions">
        {expanded ? (
          <button type="button" className="consent__button" onClick={() => save(prefs)}>
            Save &amp; Close
          </button>
        ) : (
          <>
            <button
              type="button"
              className="consent__button"
              onClick={() => save({ analytics: true, marketing: true })}
            >
              Accept
            </button>
            <button
              type="button"
              className="consent__button consent__button--ghost"
              onClick={() => setExpanded(true)}
            >
              More Options
            </button>
          </>
        )}
      </div>

      <p className="consent__links">
        <Link to="/cookies">Cookies Policy</Link>
        <Link to="/privacy">Privacy Policy</Link>
      </p>
    </div>
  );
}
